import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const viewport = document.getElementById('viewport');
const status = document.getElementById('status');
const loader = new GLTFLoader();
let renderer;
let current;
let requestId = 0;
let stopped = false;

function disposeModel(model) {
  const geometry = new Set();
  const materials = new Set();
  model?.traverse((obj) => {
    if (obj.geometry) geometry.add(obj.geometry);
    for (const mat of [].concat(obj.material || [])) materials.add(mat);
  });
  geometry.forEach((item) => item.dispose());
  materials.forEach((item) => item.dispose());
}

async function init() {
  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.3;
  viewport.append(renderer.domElement);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x17212c);
  scene.add(new THREE.HemisphereLight(0xc2d9e7, 0x535a43, 2.6));
  const key = new THREE.DirectionalLight(0xffdca8, 3.2);
  key.position.set(-40, 60, 30);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0x9dbacb, 1.7);
  fill.position.set(30, 10, -40);
  scene.add(fill);
  const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 50000);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  const resize = () => {
    camera.aspect = viewport.clientWidth / Math.max(1, viewport.clientHeight);
    camera.updateProjectionMatrix();
    renderer.setSize(viewport.clientWidth, viewport.clientHeight, false);
  };
  const observer = new ResizeObserver(resize);
  observer.observe(viewport);
  resize();
  renderer.setAnimationLoop(() => {
    if (stopped) return;
    controls.update();
    renderer.render(scene, camera);
  });
  const [manifest, layout] = await Promise.all(['manifest.json', 'world-layout.json'].map(async (file) => {
    const response = await fetch(`/assets/world/${file}`);
    if (!response.ok) throw new Error(`Unable to load ${file}`);
    return response.json();
  }));
  const assets = [...manifest.assets, { id: 'vespera-world-blockout', title: 'Full World Terrain Blockout', file: 'vespera-world-blockout.glb', triangles: 41472 }];

  async function show(asset) {
    const token = ++requestId;
    status.textContent = `Loading ${asset.title}...`;
    try {
      const result = await loader.loadAsync(`/assets/world/${asset.file}`);
      if (stopped || token !== requestId) { disposeModel(result.scene); return; }
      if (current) { scene.remove(current); disposeModel(current); }
      current = result.scene;
      scene.add(current);
      const bounds = new THREE.Box3().setFromObject(current);
      const center = bounds.getCenter(new THREE.Vector3());
      const size = bounds.getSize(new THREE.Vector3());
      const distance = Math.max(size.x, size.y, size.z) * (camera.aspect < 1.2 ? 2.1 : 1.55);
      camera.near = Math.max(0.1, distance / 5000);
      camera.far = distance * 8;
      camera.updateProjectionMatrix();
      camera.position.copy(center).add(new THREE.Vector3(distance * 0.65, distance * 0.52, distance * 0.85));
      controls.target.copy(center);
      controls.minDistance = distance * 0.08;
      controls.maxDistance = distance * 3;
      controls.update();
      document.querySelectorAll('[data-asset]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.asset === asset.id)));
      document.getElementById('asset-detail').textContent = `${asset.triangles.toLocaleString()} triangles. Metres, Y-up, glTF 2.0 binary.`;
      document.getElementById('download').href = `/assets/world/${asset.file}`;
      status.textContent = `${asset.title} ready. Orbit to inspect entrances, roofs and the surrounding layout.`;
      window.__LONEWOLF_ATLAS__ = { ready: true, asset: asset.id, triangles: asset.triangles, error: null };
    } catch (error) {
      if (token !== requestId) return;
      status.textContent = error.message;
      window.__LONEWOLF_ATLAS__ = { ready: false, error: error.message };
    }
  }

  for (const asset of assets) {
    const button = document.createElement('button');
    button.textContent = asset.title;
    button.dataset.asset = asset.id;
    button.setAttribute('aria-pressed', 'false');
    button.addEventListener('click', () => show(asset));
    document.getElementById('asset-buttons').append(button);
  }
  for (const region of layout.regions) {
    const card = document.createElement('article');
    const title = document.createElement('h3');
    title.textContent = region.name;
    const text = document.createElement('p');
    text.textContent = region.design;
    const note = document.createElement('p');
    note.className = 'note';
    note.textContent = `${region.type} / proposed level ${region.suggested_level}`;
    card.append(title, text, note);
    document.getElementById('regions').append(card);
  }
  await show(assets[0]);
  window.addEventListener('pagehide', () => {
    stopped = true;
    requestId += 1;
    observer.disconnect();
    controls.dispose();
    disposeModel(current);
    renderer.setAnimationLoop(null);
    renderer.dispose();
  }, { once: true });
}

init().catch((error) => {
  status.textContent = `3D preview unavailable: ${error.message}. The map and GLB downloads remain available.`;
  window.__LONEWOLF_ATLAS__ = { ready: false, error: error.message };
  renderer?.setAnimationLoop(null);
});
